import React, { useEffect, useRef, useState } from "react";
import api from "../../api.js";
import SectionHeader from "../../components/SectionHeader";
import Select from "../../components/Select";
import PrimaryLinkButton from "../../components/PrimaryLinkButton";
import Input from "../../components/Input";
import PrimaryButton from "../../components/PrimaryButton";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import SecondaryButton from "../../components/SecondaryButton";

const TimeInput = ({ value, onChange, title }) => {
  const startTimeRef = useRef(null);

  const handleStartTimeClick = () => {
    if (startTimeRef.current) {
      // Show native time picker if supported
      if (typeof startTimeRef.current.showPicker === "function") {
        startTimeRef.current.showPicker();
      } else {
        startTimeRef.current.focus();
      }
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {title}
      </label>
      <div className="relative">
        <Input
          type="time"
          ref={startTimeRef}
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          onClick={handleStartTimeClick}
          className="absolute right-2 top-1/2 -translate-y-1/2"
        >
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

const TimezoneInfo = ({
  companyTimeslots,
  setLoading,
  setFeedback,
  defaultValues,
}) => {
  const [values, setValues] = useState(defaultValues);

  const timezones = [
    {
      value: "America/Los_Angeles",
      label: "PST - America/Los Angeles (UTC-08:00)",
    },
    { value: "America/Denver", label: "MST - America/Denver (UTC-07:00)" },
    { value: "America/Chicago", label: "CST - America/Chicago (UTC-06:00)" },
    { value: "America/New_York", label: "EST - America/New York (UTC-05:00)" },
  ];

  const daysOfWeek = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
    { value: "every_day", label: "Every Day" },
  ];

  const [timeSlots, setTimeSlots] = useState([]);
  const [defaultTimeSlots, setDefaultTimeSlots] = useState([]);

  useEffect(() => {
    if (companyTimeslots?.length) {
      const formattedSlots = companyTimeslots.map(slot => ({
        id: slot.id,
        day: slot.day.toLowerCase(),
        startTime: slot.start_time.slice(0, 5), // Convert to HH:MM
        endTime: slot.end_time.slice(0, 5),
      }));
      setTimeSlots(formattedSlots);
      setDefaultTimeSlots(formattedSlots);
    }
  }, [companyTimeslots]);

  const handleTimeChange = (index, field, value) => {
    setTimeSlots(prev =>
      prev.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot))
    );
  };

  const addTimeSlot = () => {
    setTimeSlots([
      ...timeSlots,
      { day: "monday", startTime: "09:00", endTime: "18:00" },
    ]);
  };

  const removeTimeSlot = index => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const onSubmit = async e => {
    e.preventDefault();
    await updateUserTimeSlots(timeSlots, values.timezone);
  };

  const updateUserTimeSlots = async (timeSlots, timezone) => {
    try {
      setLoading(true);
      const payload = {
        timezone,
        time_slots: timeSlots.map(({ id, day, startTime, endTime }) => ({
          id,
          day,
          start_time: startTime,
          end_time: endTime,
        })),
      };

      const response = await api.put("/api/company/user_timeslots", payload);

      await response;
      setFeedback({
        status: "success",
        msg: "Timeslots updated successfully!",
      });
    } catch (error) {
      console.error("Error updating timeslots:", error);
    } finally {
      setLoading(false);
    }
  };

  const change = e => {
    setValues(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const endTimeRef = useRef(null);

  return (
    <form onSubmit={onSubmit}>
      <div className="flex items-start gap-6 justify-between max-sm:flex-col">
        <div className="sm:max-w-sm">
          <SectionHeader
            title="Time Zone & Hours Of Operation"
            description="Your AI agent replies at any time, but these hours will apply to
            human rep handoff. If a customer tries to contact your human team
            outside of these hours, they'll see when you'll be back online in
            their timezone."
          />
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200 w-full sm:max-w-xl">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Zone
            </label>
            <Select
              name="timezone"
              value={values.timezone}
              onChange={change}
              options={timezones}
            />
          </div>

          {timeSlots.map((slot, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center mb-3`}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day
                </label>
                <Select
                  value={slot.day}
                  onChange={e => handleTimeChange(index, "day", e.target.value)}
                  options={daysOfWeek}
                />
              </div>

              <TimeInput
                title="Start"
                value={slot.startTime}
                onChange={e =>
                  handleTimeChange(index, "startTime", e.target.value)
                }
              />

              <TimeInput
                title="End"
                value={slot.endTime}
                onChange={e =>
                  handleTimeChange(index, "endTime", e.target.value)
                }
              />

              <div
                className="flex self-end justify-center sm:justify-start"
                style={{ paddingBottom: "7px" }}
              >
                <button
                  type="button"
                  onClick={() => removeTimeSlot(index)}
                  className="text-red-600 text-xs w-6 h-6 flex items-center justify-center rounded-full border border-red-600 hover:bg-red-600 hover:text-white transition duration-200"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          <PrimaryLinkButton onClick={addTimeSlot} type="button">
            + Add hours
          </PrimaryLinkButton>

          <hr className="my-6 border-t border-gray-300" />

          <div className="flex gap-4 justify-end items-start">
            <div>
              <SecondaryButton
                onClick={() => {
                  setValues(defaultValues);
                  setTimeSlots(defaultTimeSlots);
                }}
                type="button"
              >
                Cancel
              </SecondaryButton>
            </div>

            <div>
              <PrimaryButton>Save Changes</PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default TimezoneInfo;
